import React from "react";
import { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Button, Autocomplete, TextField, FormControl, Grid, Box, Typography, IconButton, Tooltip, Chip } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  ApiPutUpdateClientEntity,
  ApiPostCreateClientEntity,
  ApiGetClients,
  ApiGetEntityTypeList,
  ApiGetBriefJson,
  ApiGetStarRating,
  ApiGetAddOnQuestions,
  ApiGetManageRegions,
  ApiGetBusinessRegions,
  ApiPostAssignRegionToClientEntity,
  ApiGetClientEntities,
} from "../../../apiService/Core/Apicall.tsx";
import {
  setLoadingFalse,
  setLoadingTrue,
} from "../../../global/config/Action/loadingActions.ts";
import imageCompression from "browser-image-compression";
import { CustomUploadProfilePic } from "../../../apiService/Core/CustomUploadProfilePic.tsx";
import store from "../../../global/config/store.ts";
import { DescriptionOutlined, InfoOutlined, InsertDriveFileOutlined, PictureAsPdfOutlined, Upload } from "@mui/icons-material";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface AddOrUpdateStoreProps {
  initialValues?: {
    locationName: string;
    code: string;
    clientName: string;
    clientPhone: string;
    clientAddress: string;
    clientState: string;
    clientPostCode: string;
    clientInvoiceEmail: string;
    fileName: string;
    pdfName: string;
    businessAdditionalInfo: string,
    templateURL: string;
    minWords: number;
    maxWords: number;
    showProductOfMonth: boolean;
    showExtraFields: boolean;
    hotelType: string;
    ownerEmail: string;
    teamBCCEmail: string;
    reportMessage: string;
    briefJson: string;
    starRatings: string;
    maxImages: number;
    minImages: number;
    hideInvoicePanel: boolean;
    hideFixIt: boolean;
    hideSiteComparison: boolean;
    canClientEditReports: boolean;
    isAllowDownloadWithoutComments: boolean;
    canAreaManagerSeeApproveReport:boolean;
    addOnQuestions: String;
    isNPSenable: boolean;
    isNPSFeedbackRequired: boolean;
    canClientSeeNPSScore: boolean;
  };
  mode: "add" | "update";
  onSubmit: (values: any) => void;
}



interface Region {
  label: string;
  value: number;
}





// Sortable Chip Component
const SortableChip = ({ id, label, onDelete }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
  };

  return (
    <Chip
      ref={setNodeRef}
      style={style}
      {...attributes}
      label={
        <span {...listeners} style={{ cursor: 'grab' }}>
          {label}
        </span>
      }
      onDelete={onDelete}
      sx={{ cursor: 'default' }}
    />
  );
};

const AddOrUpdateStore: React.FC<AddOrUpdateStoreProps> = ({
  initialValues = {
    locationName: "",
    entityCode: "",
    clientName: "",
    clientPhone: "",
    clientAddress: "",
    clientState: "",
    clientPostCode: "",
    clientInvoiceEmail: "",
    fileName: "",
    pdfName: "",
    businessAdditionalInfo: "",
    templateURL: "",
    minWords: 0,
    maxWords: 0,
    showProductOfMonth: false,
    showExtraFields: false,
    hotelType: "",
    ownerEmail: "",
    teamBCCEmail: "",
    reportMessage: "",
    briefJson: "",
    starRatings: "",
    maxImages: 0,
    minImages: 0,
    hideInvoicePanel: false,
    hideFixIt: false,
    hideSiteComparison: false,
    canClientEditReports: false,
    isAllowDownloadWithoutComments: false,
    canAreaManagerSeeApproveReport:false,
    addOnQuestions: "",
    isNPSenable:false,
    isNPSFeedbackRequired: false,
    canClientSeeNPSScore: false
  },
  onSubmit,
}) => {
  const { mode, id } = useParams<{ mode: "add" | "update", id: any }>();

  const location = useLocation();
  // const clientEntity = location?.state?.clientEntity;
  const [clientEntity, setClientEntity] = useState<any>([])
  const [formValues, setFormValues] = useState(initialValues);
  const [clients, setClients] = useState([]);
  const [entityType, setEntityType] = useState<any[]>([]);
  const [clientName, setClientName] = useState("");
  const [hotelType, setHotelType] = useState("");
  const [starRatingList, setStarRating] = useState<any>([]);
  const [addOnQuestionsList, setAddOnQuestionsList] = useState<any>([]);
  const navigate = useNavigate();
  const [reportLogoUrl, setReportLogoUrl] = useState("");
  const [reportLogoPreview, setReportLogoPreview] = useState<string | null>(
    null
  );
  const [reportHeaderImageUrl, setReportHeaderImageUrl] = useState("");
  const [reportHeaderImagePreview, setReportHeaderImagePreview] = useState<
    string | null
  >(null);
  const [reportCoverImageUrl, setReportCoverImageUrl] = useState("");
  const [reportCoverImagePreview, setReportCoverImagePreview] = useState<
    string | null
  >(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [assignedRegions, setAssignedRegions] = useState<number[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>(clientEntity?.sampleReportUrl
    ? clientEntity.sampleReportUrl.split(",").filter(Boolean)
    : []
  );

  const validationSchema = Yup.object().shape({
    minWords: Yup.number()
      .min(0, "Minimum words must be at least 0")
      .test(
        "min-max-words",
        "Minimum words cannot be greater than maximum words",
        function (minWords) {
          const { maxWords } = this.parent;
          return !minWords || !maxWords || minWords <= maxWords;
        }
      ),
    maxWords: Yup.number().min(0, "Maximum words must be at least 0"),
    minImages: Yup.number()
      .min(0, "Minimum images must be at least 0")
      .test(
        "min-max-images",
        "Minimum images cannot be greater than maximum images",
        function (minImages) {
          const { maxImages } = this.parent;
          return !minImages || !maxImages || minImages <= maxImages;
        }
      ),
    maxImages: Yup.number().min(0, "Maximum images must be at least 0"),
  });

  const handleGetClientList = async () => {
    const data = `?clientEntityId=${id}`;
    try {
      const response: any = await ApiGetClientEntities(data);
      setClientEntity(response.data[0]);
      setUploadedFiles(response.data[0].sampleReportUrl.split(",").filter(Boolean))
      // console.log("rse store", response.data);
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  useEffect(() => {
    id && handleGetClientList()
  }, [id])

  useEffect(() => {
    if (mode === "update" && clientEntity) {
      setClientName(clientEntity?.clientId?.ClientID || "");
      setHotelType(clientEntity?.entityTypeId?.entityTypeId || "");
      setFormValues({
        locationName: clientEntity?.entityName || "",
        entityCode: clientEntity?.entityCode || "",
        clientName: clientEntity?.clientId?.ClientID || "",
        clientPhone: clientEntity?.contactNo || "",
        clientAddress: clientEntity?.address || "",
        clientState: clientEntity?.state || "",
        clientPostCode: clientEntity?.postalCode || "",
        clientInvoiceEmail: clientEntity?.emailId || "",
        fileName: clientEntity?.fileName || "",
        pdfName: clientEntity?.pdfName || "",
        businessAdditionalInfo: clientEntity?.businessAdditionalInfo || "",
        templateURL: clientEntity?.templateURL || "",
        minWords: clientEntity?.minNoofWords || 0,
        maxWords: clientEntity?.maxNoofWords || 0,
        showProductOfMonth: clientEntity?.showPom || false,
        showExtraFields: clientEntity?.extraFields || false,
        hotelType: clientEntity?.entityTypeId?.entityTypeId || "",
        ownerEmail: clientEntity?.ownerEmail || "",
        teamBCCEmail: clientEntity?.bccEmailId || "",
        reportMessage: clientEntity?.reportMessage || "",
        briefJson: clientEntity?.briefJson || "",
        starRatings: clientEntity?.ratingJson || "",
        maxImages: clientEntity?.maxNoOfImages || 0,
        minImages: clientEntity?.minNoOfImages || 0,
        hideInvoicePanel: clientEntity?.invoicePanel || false,
        hideFixIt: clientEntity?.hideFixIt || false,
        hideSiteComparison: clientEntity?.hideSiteComparison || false,
        canClientEditReports: clientEntity?.canClientEditReports || false,
        isAllowDownloadWithoutComments: clientEntity?.isAllowDownloadWithoutComments || false,
        canAreaManagerSeeApproveReport: clientEntity?.canAreaManagerSeeApproveReport || false,
        addOnQuestions: clientEntity?.addOnQuestionIds || "",
        isNPSenable: clientEntity?.isNPSenable || false,
        isNPSFeedbackRequired: clientEntity?.isNPSFeedbackRequired || false,
        canClientSeeNPSScore: clientEntity?.canClientSeeNPSScore || false 
      });
      setReportLogoUrl(clientEntity?.reportLogo);
      setReportHeaderImageUrl(clientEntity?.reportHeaderImage);
      setReportCoverImageUrl(clientEntity?.reportCoverImage);
      handleGetAssignedRegions();
    }
  }, [mode, clientEntity]);

  useEffect(() => {
    if (clientEntity) {
    }
    handleGetApiCallClients();
    handleGetApiCallEntityType();
    handelApiGetStarRating();
    handleGetEditType();
    handleGetAddOnQuestions();
    handleGetRegions();
  }, [clientEntity, mode]);

  const handleGetRegions = async () => {
    try {
      const response: any = await ApiGetManageRegions();
      const transformed = response.data.map((item: any) => ({
        value: item.regionId,
        label: item.regionName,
      }));
      setRegions(transformed);
    } catch (error) {
      console.error("Error fetching regions:", error);
    }
  };

  const handleGetAssignedRegions = async () => {
    if(!clientEntity.clientEntityId) return;
    try {
      const response: any = await ApiGetBusinessRegions(
        `?clientEntityId=${clientEntity.clientEntityId}`
      );
      const assignedIds = response.data.map((item: any) => item.regionId);
      setAssignedRegions(assignedIds);
    } catch (error) {
      console.error("Error fetching assigned regions:", error);
    }
  };

  const handleGetApiCallClients = async () => {
    const data = "?offset=0&limit=1000&status=active";
    try {
      const response: any = await ApiGetClients(data);
      setClients(
        response.data.map((client: any) => ({
          label: client.ClientName,
          value: client.ClientID,
        }))
      );
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const handelApiGetStarRating = async () => {
    try {
      const data = `?offset=0&limit=1000`
      const response: any = await ApiGetStarRating(data);
      setStarRating(
        response.data.map((starRating: any) => ({
          label: starRating.starRatingDesc,
          value: starRating.starRatingId,
          isDisabled: starRating.isDisabled
        }))
      );
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const handleGetAddOnQuestions = async () => {
    try {
      const response: any = await ApiGetAddOnQuestions();
      setAddOnQuestionsList(
        response.data.map((addOnQuestion: any) => ({
          label: addOnQuestion.addOnQuestionText,
          value: addOnQuestion.addOnQuestionId,
        }))
      );
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const handleGetApiCallEntityType = async () => {
    try {
      const response: any = await ApiGetEntityTypeList();

      const transformedData = response.data.map((item: any) => ({
        value: item.entityTypeId,
        label: item.entityTypeName,
      }));
      setEntityType(transformedData);
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const handleAddStore = async (values: any) => {

    const FormData = {
      entityName: values.locationName,
      entityCode: values.entityCode,
      clientId: clientName,
      contactNo: values.clientPhone,
      emailId: values.clientInvoiceEmail,
      address: values.clientAddress,
      city: "",
      state: values.clientState,
      postalCode: values.clientPostCode,
      country: "",
      lat: "",
      long: "",
      fileName: values.fileName,
      pdfName: values.pdfName,
      businessAdditionalInfo: values.businessAdditionalInfo,
      minNoofWords: values.minWords,
      maxNoofWords: values.maxWords,
      showPom: values.showProductOfMonth,
      extraFields: values.showExtraFields,
      entityTypeId: hotelType,
      bccEmailId: values.teamBCCEmail,
      briefJson: values.briefJson,
      ratingJson: values.starRatings,
      maxNoOfImages: values.maxImages,
      minNoOfImages: values.minImages,
      invoicePanel: values.hideInvoicePanel,
      hideFixIt: values.hideFixIt,
      hideSiteComparison: values.hideSiteComparison,
      canClientEditReports: values.canClientEditReports,
      isAllowDownloadWithoutComments: values.isAllowDownloadWithoutComments,
      canAreaManagerSeeApproveReport: values.canAreaManagerSeeApproveReport,
      addOnQuestionIds: values.addOnQuestions,
      regionIds: assignedRegions,
      createdBy: Number(localStorage.getItem("createdBy")),
      sampleReportUrl: uploadedFiles.join(","),
      isNPSenable: values.isNPSenable,
      isNPSFeedbackRequired: values.isNPSFeedbackRequired,
      canClientSeeNPSScore: values.canClientSeeNPSScore
    };
    try {
      const response = await ApiPostCreateClientEntity(FormData);
      toast.success("Store added successfully!");
      navigate(-1);
    } catch (error) {
      console.error("Error adding store:", error);
      toast.error(error);
    }
  };

  const handleUpdateStore = async (values: any) => {
    const data = {
      clientEntityId: clientEntity.clientEntityId,
      regionIds: assignedRegions,
      createdBy: Number(localStorage.getItem("createdBy")),
    };
    try {
      await ApiPostAssignRegionToClientEntity(data);
    } catch (error) {
      console.error("Error during API call:", error);
    }

    if (values.minWords > values.maxWords) {
      toast.error("min Words should not be greater then max words!");
      return;
    }

    if (values.minImages > values.maxImages) {
      toast.error("min Image should not be greater then max Image!");
      return;
    }

    const FormData = {
      clientEntityId: clientEntity?.clientEntityId,
      entityName: values.locationName,
      entityCode: values.entityCode,
      clientId: clientName,
      contactNo: values.clientPhone,
      emailId: values.clientInvoiceEmail,
      address: values.clientAddress,
      city: "",
      state: values.clientState,
      postalCode: values.clientPostCode,
      country: "",
      lat: "",
      long: "",
      fileName: values.fileName,
      additionalinfo: values.additionalinfo,
      pdfName: values.pdfName,
      businessAdditionalInfo: values.businessAdditionalInfo,
      minNoofWords: values.minWords,
      maxNoofWords: values.maxWords,
      showPom: values.showProductOfMonth,
      extraFields: values.showExtraFields,
      entityTypeId: hotelType,
      bccEmailId: values.teamBCCEmail,
      briefJson: values.briefJson,
      ratingJson: values.starRatings,
      maxNoOfImages: values.maxImages,
      minNoOfImages: values.minImages,
      invoicePanel: values.hideInvoicePanel,
      hideFixIt: values.hideFixIt,
      hideSiteComparison: values.hideSiteComparison,
      canClientEditReports: values.canClientEditReports,
      isAllowDownloadWithoutComments: values.isAllowDownloadWithoutComments,
      canAreaManagerSeeApproveReport: values.canAreaManagerSeeApproveReport,
      addOnQuestionIds: values.addOnQuestions,
      reportLogo: reportLogoUrl,
      reportHeaderImage: reportHeaderImageUrl,
      reportCoverImage: reportCoverImageUrl,
      regionIds: assignedRegions,
      createdBy: Number(localStorage.getItem("createdBy")),
      sampleReportUrl: uploadedFiles.join(","),
      isNPSenable: values.isNPSenable,
      isNPSFeedbackRequired: values.isNPSFeedbackRequired,
      canClientSeeNPSScore: values.canClientSeeNPSScore

    };
    try {
      const response = await ApiPutUpdateClientEntity(FormData);
      toast.success("Store Update successfully!");
      navigate(-1);
    } catch (error) {
      console.error("Error updating store:", error);
    }
  };

  const [briefJsonNew, setBriefJson] = useState<any>([]);


  // console.log("BriefJson Order---->", formValues.briefJson)
  const handleGetEditType = async () => {
    const data = "?offset=0&limit=500"
    try {
      const response: any = await ApiGetBriefJson(data);
      if (Array.isArray(response.data)) {
        const transformedData = response.data.map((item: any) => ({
          value: item.briefJsonId,
          label: item.briefJsonTitle,
          min: item.minNoOfWords,
          max: item.maxNoOfWords,
          description: item?.briefJsonDescription
        }));
        setBriefJson(transformedData);
      } else {
        console.error("Unexpected data format:", response.data);
        setBriefJson([]);
      }
    } catch (error) {
      console.error("Error during API call:", error);
      setBriefJson([]);
    }
  };

  const uploadReportLogo = async (file: File) => {
    store.dispatch(setLoadingTrue());
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileType", "report_logo");
    if (mode === "update") {
      formData.append("clientEntityId", clientEntity?.clientEntityId);
    }

    try {
      const response = await CustomUploadProfilePic(formData);
      store.dispatch(setLoadingFalse());
      toast.success("Report logo uploaded successfully");
      setReportLogoUrl(response.location);
    } catch (error) {
      console.error("Error uploading report logo:", error);
      toast.error(error);
    }
  };

  const uploadReportHeaderImage = async (file: File) => {
    store.dispatch(setLoadingTrue());
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileType", "report_header_image");
    if (mode === "update") {
      formData.append("clientEntityId", clientEntity?.clientEntityId);
    }

    try {
      const response = await CustomUploadProfilePic(formData);
      store.dispatch(setLoadingFalse());
      toast.success("Report header image uploaded successfully");
      setReportHeaderImageUrl(response.location);
    } catch (error) {
      console.error("Error uploading report header image:", error);
      toast.error(error);
    }
  };

  const uploadReportCoverImage = async (file: File) => {
    store.dispatch(setLoadingTrue());
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileType", "report_cover_image");
    if (mode === "update") {
      formData.append("clientEntityId", clientEntity?.clientEntityId);
    }

    try {
      const response = await CustomUploadProfilePic(formData);
      store.dispatch(setLoadingFalse());
      toast.success("Report cover image uploaded successfully");
      setReportCoverImageUrl(response.location);
    } catch (error) {
      console.error("Error uploading report cover image:", error);
      toast.error(error);
    }
  };

  const handleReportLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      store.dispatch(setLoadingTrue());
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          setReportLogoPreview(fileReader.result as string);
        };
        fileReader.readAsDataURL(compressedFile);
        uploadReportLogo(compressedFile);
      } catch (error) {
        console.error("Report logo compression failed:", error);
      }
    }
  };

  const handleReportHeaderImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      store.dispatch(setLoadingTrue());
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          setReportHeaderImagePreview(fileReader.result as string);
        };
        fileReader.readAsDataURL(compressedFile);
        uploadReportHeaderImage(compressedFile);
      } catch (error) {
        console.error("Report header image compression failed:", error);
      }
    }
  };

  const handleReportCoverImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      store.dispatch(setLoadingTrue());
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          setReportCoverImagePreview(fileReader.result as string);
        };
        fileReader.readAsDataURL(compressedFile);
        uploadReportCoverImage(compressedFile);
      } catch (error) {
        console.error("Report cover image compression failed:", error);
      }
    }
  };

  const uploadDocumentImage = async (file: File) => {
    store.dispatch(setLoadingTrue())
    const formData = new FormData()
    const userId: any = localStorage.getItem('userId')
    formData.append("file", file)
    formData.append("fileType", "project_static_files")
    formData.append("userId", userId)

    try {
      const response = await CustomUploadProfilePic(formData)
      store.dispatch(setLoadingFalse())
      toast.success(response.message)
      // Add the uploaded file URL to the state
      setUploadedFiles(prev => [...prev, response.location]);
    } catch (error) {
      console.error("Error uploading file:", error)
      toast.error("Error Uploading File")
    }
  }

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null
    if (file) {
      store.dispatch(setLoadingTrue())
      try {
        await uploadDocumentImage(file)
      } catch (error) {
        console.error("Image upload failed:", error)
      }
    }
  }

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => {
      const updatedFiles = [...prev];
      updatedFiles.splice(index, 1);
      return updatedFiles;
    });
  };


  return (
    <Formik
      initialValues={formValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => {
        // console.log("Form submitted with values:", values);
        if (mode === "add") {
          // console.log("Calling handleAddStore");
          handleAddStore(values);
        } else {
          // console.log("Calling handleUpdateStore");
          handleUpdateStore(values);
        }
      }}
    >
      {({ handleChange, handleBlur, values }) => (
        <Form className="m-2 py-3" style={{ background: "white" }}>
          <h1 className="common-add mb-5">
            {mode === "add" ? "Add New Business" : "Update Business"}
          </h1>
          <Box className='w-100 px-2'>
            <div className="menage-store-select">
              <Autocomplete
                options={clients}
                value={
                  clients.find((option: any) => option.value === clientName) ||
                  null
                }
                getOptionLabel={(option: any) => option.label}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setClientName(newValue.value);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Client" />
                )}
                fullWidth
                disabled={mode === "update"}
              />
              <Autocomplete
                options={entityType}
                style={{ marginTop: "15px" }}
                value={
                  entityType.find((option) => option.value === hotelType) || null
                }
                getOptionLabel={(option) => option.label}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setHotelType(newValue.value);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Entity Type" />
                )}
                fullWidth
                disabled={mode === "update"}
              />
            </div>
            <Box style={{ padding: "20px 0px", width: "100%", paddingBottom: "0px" }}>
              {[
                { label: "Location Name", name: "locationName", type: "text" },
                { label: "Entity Code", name: "entityCode", type: "text" },
                { label: "Client Phone", name: "clientPhone", type: "text" },
                { label: "Client Address", name: "clientAddress", type: "text" },
                { label: "Client State", name: "clientState", type: "text" },
                {
                  label: "Client PostCode",
                  name: "clientPostCode",
                  type: "text",
                },
                {
                  label: "Client Invoice Email",
                  name: "clientInvoiceEmail",
                  type: "email",
                },
                { label: "File Name", name: "fileName", type: "text" },
                { label: "PDF Name", name: "pdfName", type: "text" },
                { label: "Additional Info", name: "businessAdditionalInfo", type: "text" },
                // { label: 'Template URL', name: 'templateURL', type: 'url' },
                { label: "Min. No. of Words", name: "minWords", type: "number" },
                { label: "Max. No. of Words", name: "maxWords", type: "number" },
                {
                  label: "Show Product of Month?",
                  name: "showProductOfMonth",
                  type: "checkbox",
                },
                {
                  label: "Show Extra Fields?",
                  name: "showExtraFields",
                  type: "checkbox",
                },
                { label: "Team BCC Email", name: "teamBCCEmail", type: "text" },
                // {
                //   label: 'Report Message',
                //   name: 'reportMessage',
                //   type: 'textarea'
                // },
                { label: "Brief JSON", name: "briefJson", type: "Brief" },
                { label: "Star Ratings", name: "starRatings", type: "string" },
                {
                  label: "Add on Questions",
                  name: "addOnQuestions",
                  type: "addOnQuestions",
                },
                {
                  label: "Region",
                  name: "region",
                  type: "region",
                },
                {
                  label: "Max. No. of Images",
                  name: "maxImages",
                  type: "number",
                },
                {
                  label: "Min. No. of Images",
                  name: "minImages",
                  type: "number",
                },
                {
                  label: "Hide Invoice Panel?",
                  name: "hideInvoicePanel",
                  type: "checkbox",
                },
                {
                  label: "Hide Site Comparison ?",
                  name: "hideSiteComparison",
                  type: "checkbox",
                },
                {
                  label: "Hide Fix It?",
                  name: "hideFixIt",
                  type: "checkbox",
                },
                {
                  label: "Client Edit Reports?",
                  name: "canClientEditReports",
                  type: "checkbox",
                },
                {
                  label: "Allow Download Without Comments?",
                  name: "isAllowDownloadWithoutComments",
                  type: "checkbox",
                },
                {
                  label: "Area Manager See Approve Report?",
                  name: "canAreaManagerSeeApproveReport",
                  type: "checkbox",
                },
                {
                  label: "NPS Enable?",
                  name: "isNPSenable",
                  type: "checkbox",
                },
                {
                  label: "Can Client See NPS Score?",
                  name: "canClientSeeNPSScore",
                  type: "checkbox",
                },
                {
                  label: "Is NPS Feedback Required?",
                  name: "isNPSFeedbackRequired",
                  type: "checkbox",
                },
              ].map(({ label, name, type }) => (
                <Box
                  key={name}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "center",
                    marginBottom: "15px",
                    width: "100%",
                  }}
                >
                  <label
                    htmlFor={name}
                    className="form-label"
                    style={{
                      marginRight: "10px",
                      minWidth: "200px",
                    }}
                  >
                    {label}:
                  </label>
                  {type === "Brief" ? (
                    // <Autocomplete
                    //   multiple
                    //   options={briefJsonNew}
                    //   sx={{ marginTop: "15px", width: { xs: "100%", md: "calc(100% - 220px)" } }}
                    //   value={
                    //     values.briefJson
                    //       .split(",")
                    //       .map((val) => val.trim())
                    //       .map((val) =>
                    //         briefJsonNew.find((editor: any) => String(editor.value) === val)
                    //       )
                    //   }
                    //   getOptionLabel={(option) => option.label}
                    //   renderOption={(props, option) => (
                    //     <Tooltip title={option?.description}>
                    //       <li {...props}>
                    //         <span style={{ marginRight: 12 }}>
                    //           {option.label}
                    //         </span>

                    //         <span className="text-danger" style={{ marginRight: 12, fontSize: '14px', fontWeight: 'bold' }}>
                    //           Min: {option.min}, Max: {option.max},
                    //         </span>
                    //         <span style={{ fontSize: '14px' }}>
                    //           <b className="text-danger">Description:{" "}</b>
                    //           {option.description?.split(" ").slice(0, 20).join(" ")}
                    //           {option.description?.split(" ").length > 20 && " ..."}
                    //         </span>
                    //       </li>
                    //     </Tooltip>
                    //   )}
                    //   onChange={(event, newValue) => {
                    //     const selectedValues = newValue.map(
                    //       (option) => option.value
                    //     );
                    //     handleChange({
                    //       target: {
                    //         name: "briefJson",
                    //         value: selectedValues.join(","),
                    //       },
                    //     });
                    //   }}
                    //   renderInput={(params) => (
                    //     <TextField {...params} label="Select briefJson" />
                    //   )}
                    //   fullWidth
                    // />
                    <Autocomplete
                      multiple
                      options={briefJsonNew}
                      sx={{ marginTop: "15px", width: { xs: "100%", md: "calc(100% - 220px)" } }}
                      value={values.briefJson
                        .split(",")
                        .map((val) => val.trim())
                        .map((val) =>
                          briefJsonNew.find((editor: any) => String(editor.value) === val)
                        )
                        .filter(Boolean)
                      }
                      getOptionLabel={(option) => option.label}
                      renderOption={(props, option) => (
                        <Tooltip title={option?.description}>
                          <li {...props}>
                            <span style={{ marginRight: 12, whiteSpace: "nowrap" }}>
                              {option.label}
                            </span>

                            <span className="text-danger" style={{ marginRight: 12, fontSize: '14px', fontWeight: 'bold', whiteSpace: "nowrap" }}>
                              Min: {option.min}, Max: {option.max},
                            </span>
                            <span style={{ fontSize: '14px' }}>
                              <b className="text-danger">Description:{" "}</b>
                              {option.description?.split(" ").slice(0, 20).join(" ")}
                              {option.description?.split(" ").length > 20 && " ..."}
                            </span>
                          </li>
                        </Tooltip>
                      )}

                      renderTags={(value, getTagProps) => (
                        <DndContext
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => {
                            const { active, over } = event;
                            if (active.id !== over?.id) {
                              const oldIndex = value.findIndex(
                                (item) => item.value === active.id
                              );
                              const newIndex = value.findIndex(
                                (item) => item.value === over?.id
                              );

                              const newOrder = arrayMove(value, oldIndex, newIndex);

                              const selectedValues = newOrder.map((item) => item.value);

                              handleChange({
                                target: {
                                  name: "briefJson",
                                  value: selectedValues.join(","),
                                },
                              });
                            }
                          }}
                        >
                          <SortableContext
                            items={value.map((item) => item.value)}
                            strategy={horizontalListSortingStrategy}
                          >
                            {value.map((option, index) => {
                              const tagProps = getTagProps({ index });
                              return (
                                <Tooltip arrow key={index} title={
                                  <div>
                                    <p style={{ margin: 0 }}>{option?.description}</p>
                                    <b>Min:</b> {option?.min} <br />
                                    <b>Max:</b> {option?.max}
                                  </div>
                                }>
                                  <span>
                                    <SortableChip
                                      key={option.value}
                                      id={option.value}
                                      label={option.label}
                                      onDelete={tagProps.onDelete}
                                    />
                                  </span>
                                </Tooltip>
                              )
                            })}
                          </SortableContext>
                        </DndContext>
                      )}

                      onChange={(event, newValue) => {
                        const selectedValues = newValue.map(
                          (option) => option.value
                        );
                        handleChange({
                          target: {
                            name: "briefJson",
                            value: selectedValues.join(","),
                          },
                        });
                      }}

                      renderInput={(params) => (
                        <TextField {...params} label={
                          <Box display="flex" alignItems="center" gap={1}>
                            Select briefJson
                            <Tooltip title="Drag and drop to reorder the selected items.">
                              <InfoOutlined
                                fontSize="small"
                                sx={{ cursor: "pointer", color: "#ADB792" }}
                              />
                            </Tooltip>
                          </Box>
                        } />
                      )}
                      fullWidth
                    />

                  ) : type === "textarea" ? (
                    <TextField
                      // as="textarea"
                      multiline
                      id={name}
                      name={name}
                      sx={{ width: "100%", height: "80px" }}
                      value={values[name]}
                      className="feild"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  ) : type === "checkbox" ? (
                    <Field
                      type="checkbox"
                      id={name}
                      name={name}
                      style={{ width: "auto", margin: "0 10px" }}
                      checked={values[name]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  ) : type === "number" ? (
                    <Box sx={{ width: { xs: "100%", md: "calc(100% - 220px)" } }}>
                      <Field
                        type={type}
                        id={name}
                        name={name}
                        style={{
                          width: "100%",
                          padding: "5px",
                        }}
                        value={values[name]}
                        min="0"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (
                            value === "" ||
                            (Number(value) >= 0 && /^\d*$/.test(value))
                          ) {
                            handleChange(e);
                          }
                        }}
                        onBlur={handleBlur}
                      />
                      {name === "minWords" ||
                        name === "maxWords" ||
                        name === "minImages" ||
                        name === "maxImages" ? (
                        <ErrorMessage name={name}>
                          {(msg) => (
                            <div style={{ color: "red", fontSize: "12px" }}>
                              {msg}
                            </div>
                          )}
                        </ErrorMessage>
                      ) : null}
                    </Box>
                  ) : name === "clientInvoiceEmail" ? (
                    <TextField
                      type={type}
                      id={name}
                      name={name}
                      className="feild"
                      sx={{
                        width: { xs: "100%", md: "calc(100% - 220px)" },
                        padding: "5px",
                      }}
                      value={values[name]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={mode === "update"}
                    />
                  ) : type === "string" ? (
                    // <Autocomplete
                    //   multiple
                    //   id="star-ratings-multiple"
                    //   options={starRatingList}
                    //   sx={{ marginTop: "15px", width: { xs: "100%", md: "calc(100% - 220px)" } }}
                    //   value={starRatingList.filter((option) =>
                    //     values.starRatings
                    //       .split(",")
                    //       .map((val) => val.trim())
                    //       .includes(String(option.value))
                    //   )}
                    //   getOptionLabel={(option) => option.label}
                    //   onChange={(event, newValues) => {
                    //     const selectedValues = newValues.map(
                    //       (option) => option.value
                    //     );
                    //     handleChange({
                    //       target: {
                    //         name: "starRatings",
                    //         value: selectedValues.join(","),
                    //       },
                    //     });
                    //   }}
                    //   renderInput={(params) => (
                    //     <TextField {...params} label="Select Star Ratings" />
                    //   )}
                    //   fullWidth
                    // />
                    <Autocomplete
                      multiple
                      id="star-ratings-multiple"
                      options={starRatingList?.filter((item: any) => !item?.isDisabled)}
                      sx={{
                        marginTop: "15px",
                        width: { xs: "100%", md: "calc(100% - 220px)" },
                      }}

                      value={values.starRatings
                        ?.split(",")
                        .map((val) => val.trim())
                        .map((val) =>
                          starRatingList.find(
                            (option) => String(option.value) === val
                          )
                        )
                        .filter(Boolean)
                      }

                      getOptionLabel={(option) => option.label}

                      renderTags={(value, getTagProps) => (
                        <DndContext
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => {
                            const { active, over } = event;
                            if (over && active.id !== over.id) {
                              const oldIndex = value.findIndex(
                                (item) => item.value === active.id
                              );
                              const newIndex = value.findIndex(
                                (item) => item.value === over.id
                              );

                              const newOrder = arrayMove(value, oldIndex, newIndex);

                              const selectedValues = newOrder.map(
                                (item) => item.value
                              );

                              handleChange({
                                target: {
                                  name: "starRatings",
                                  value: selectedValues.join(","),
                                },
                              });
                            }
                          }}
                        >
                          <SortableContext
                            items={value.map((item) => item.value)}
                            strategy={horizontalListSortingStrategy}
                          >
                            {value.map((option, index) => {
                              const tagProps = getTagProps({ index });
                              return (
                                <SortableChip
                                  key={option.value}
                                  id={option.value}
                                  label={option.label}
                                  onDelete={tagProps.onDelete}
                                />
                              )
                            })}
                          </SortableContext>
                        </DndContext>
                      )}

                      onChange={(event, newValues) => {
                        const selectedValues = newValues.map(
                          (option) => option.value
                        );
                        handleChange({
                          target: {
                            name: "starRatings",
                            value: selectedValues.join(","),
                          },
                        });
                      }}

                      renderInput={(params) => (
                        <TextField {...params} label={
                          <Box display="flex" alignItems="center" gap={1}>
                            Select Star Ratings
                            <Tooltip title="Drag and drop to reorder the selected items.">
                              <InfoOutlined
                                fontSize="small"
                                sx={{ cursor: "pointer", color: "#ADB792" }}
                              />
                            </Tooltip>
                          </Box>
                        } />
                      )}

                      fullWidth
                    />
                  ) : type === "addOnQuestions" ? (
                    <Autocomplete
                      multiple
                      options={addOnQuestionsList}
                      sx={{ marginTop: "15px", width: { xs: "100%", md: "calc(100% - 220px)" } }}
                      value={addOnQuestionsList.filter((option) =>
                        values.addOnQuestions
                          .split(",")
                          .map((val) => val.trim())
                          .includes(String(option.value))
                      )}
                      getOptionLabel={(option) => option.label}
                      onChange={(event, newValues) => {
                        const selectedValues = newValues.map((option) =>
                          String(option.value)
                        );
                        handleChange({
                          target: {
                            name: "addOnQuestions",
                            value: selectedValues.join(","),
                          },
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Add On Questions" />
                      )}
                      fullWidth
                    />
                  ) : type === "region" ? (
                    <Autocomplete
                      multiple
                      options={regions}
                      getOptionLabel={(option) => option.label}
                      value={regions.filter((region: any) =>
                        assignedRegions.includes(region.value)
                      )}
                      onChange={(event, selected) => {
                        const selectedValues = selected.map(
                          (item: any) => item.value
                        );
                        setAssignedRegions(selectedValues);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Regions" />
                      )}
                      fullWidth
                    />
                  ) : (
                    // <Field
                    //   type={type}
                    //   id={name}
                    //   name={name}
                    //   style={{
                    //     width: {xs:"100%",md:"calc(100% - 220px)"},
                    //     padding: "5px",
                    //   }}
                    //   value={values[name]}
                    //   onChange={handleChange}
                    //   onBlur={handleBlur}
                    // />
                    <TextField
                      type={type}
                      id={name}
                      name={name}
                      sx={{
                        width: { xs: "100%", md: "calc(100% - 220px)" },
                        padding: "5px",
                      }}
                      className="feild"
                      value={values[name]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  )}
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
                marginBottom: '15px',
                width: "100%",
              }}
            >
              <label
                htmlFor="reportLogo"
                className="form-label"
                style={{
                  marginRight: "10px",
                  minWidth: "200px",

                }}
              >
                Report Logo:
              </label>
              <Box className='w-100 '>
                <TextField
                  type="file"
                  // accept="image/*"
                  inputProps={{ accept: "image/*" }}
                  onChange={(event) => {
                    handleReportLogoChange(event);
                  }}
                  sx={{ width: { xs: "100%", md: "100%" } }}
                />
                <span style={{ fontSize: "12px" }}>Dimensions: height - 3.175 cm (120 px)</span>
              </Box>
            </Box>

            {reportLogoUrl && (
              <div style={{ margin: "20px" }} className="text-center">
                <img
                  src={reportLogoUrl || "/placeholder.svg"}
                  alt="Report Logo Preview"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "200px",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
                marginBottom: "15px",
                width: "100%",
              }}
            >
              <label
                htmlFor="reportHeaderImage"
                style={{
                  marginRight: "10px",
                  minWidth: "200px",
                  textAlign: "right",
                }}
              >
                Report Header Image:
              </label>
              <Box className='w-100 '>
                <TextField
                  type="file"
                  // accept="image/*"
                  inputProps={{ accept: "image/*" }}
                  onChange={(event) => {
                    handleReportHeaderImageChange(event);
                  }}
                  sx={{ width: { xs: "100%", md: "100%" } }}
                />
                <span style={{ fontSize: "12px" }}>Dimensions: height - 6.61cm (250 px),  width -  21.17cm (800px)</span>
              </Box>
            </Box>

            {reportHeaderImageUrl && (
              <div style={{ margin: "20px" }} className="text-center">
                <img
                  src={reportHeaderImageUrl || "/placeholder.svg"}
                  alt="Report Header Image Preview"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "200px",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
                marginBottom: "15px",
                width: "100%",
              }}
            >
              <label
                htmlFor="reportCoverImage"
                style={{
                  marginRight: "10px",
                  minWidth: "200px",
                  textAlign: "right",
                }}
              >
                Report Cover Image:
              </label>
              <Box className='w-100'>
                <TextField
                  type="file"
                  // accept="image/*"
                  inputProps={{ accept: "image/*" }}
                  onChange={(event) => {
                    handleReportCoverImageChange(event);
                  }}
                  sx={{ width: { xs: "100%", md: "100%" } }}
                />
                <span style={{ fontSize: "12px" }}> Dimensions: height - 15.875 cm (600 px),  width -  21.17cm (800px)</span>
              </Box>

            </Box>

            {reportCoverImageUrl && (
              <div style={{ margin: "20px" }} className="text-center">
                <img
                  src={reportCoverImageUrl || "/placeholder.svg"}
                  alt="Report Cover Image Preview"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "200px",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
                marginBottom: "15px",
                width: "100%",
              }}
            >
              <label
                style={{
                  marginRight: "10px",
                  minWidth: "200px",
                  textAlign: "right",
                }}
              >
                Sample Report:
              </label>
              <FormControl className='mt-3' fullWidth>
                <Grid container className='w-100' spacing={2}>
                  {uploadedFiles.map((fileUrl, index) => (
                    <Grid size={12} key={index}>
                      <Box
                        className='w-100'
                        sx={{
                          position: 'relative',
                          border: '2px dashed #ccc',
                          borderRadius: '10px',
                          overflow: 'hidden',
                          height: '150px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          p: 2
                        }}
                      >
                        {fileUrl.includes(".pdf") ? (
                          <PictureAsPdfOutlined color="error" fontSize="large" />
                        ) : fileUrl.includes(".xlsx") || fileUrl.includes(".xls") ? (
                          <DescriptionOutlined color="success" fontSize="large" />
                        ) : (
                          <InsertDriveFileOutlined color="action" fontSize="large" />
                        )}

                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            maxWidth: '100%',
                            mt: 1,
                            textDecoration: 'underline',
                            color: 'primary.main',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(fileUrl, '_blank')}
                        >
                          {fileUrl.split('/').pop()}
                        </Typography>

                        <IconButton
                          onClick={() => handleRemoveFile(index)}
                          sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            bgcolor: 'white',
                            '&:hover': { bgcolor: 'grey.100' },
                          }}
                        >
                          <CancelOutlinedIcon color="error" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}

                  {uploadedFiles.length === 0 && (
                    <Grid size={12}>
                      <label htmlFor="help_document" className="w-100" style={{ fontSize: '14px', color: 'grey' }}>
                        <Box
                          className="d-flex flex-column align-items-center justify-content-center p-5 w-100"
                          sx={{
                            border: '2px dashed #ccc',
                            borderRadius: '10px',
                            position: 'relative',
                            height: '150px'
                          }}
                        >
                          <input
                            type="file"
                            style={{ opacity: 0, position: 'absolute' }}
                            className="w-100 h-100"
                            id="help_document"
                            onChange={handleDocumentUpload}
                          />
                          <Upload />
                          <div>Upload Here</div>
                        </Box>
                      </label>
                    </Grid>
                  )}
                </Grid>
              </FormControl>
            </Box>
            <div className="text-center">
              <Button
                style={{ padding: "9px 70px" }}
                type="submit"
                variant="contained"
                color="primary"
                className="table-row-background "
              >
                {mode === "add" ? "Add Business" : "Update Business"}
              </Button>
            </div>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default AddOrUpdateStore;
